import { supabase } from '../../lib/supabase';
import type { UnderLease } from '../../types/underlease';

interface CreateUnderLeaseData {
  underlease_lease: number;
  underlease_holder: number;
  underlease_start: string;
  underlease_end: string;
  underlease_use: string;
}

export const supabaseUnderLeaseService = {
  async getAll() {
    try {
      // Phase 1: Get the basic data without complex joins (this works)
      console.log("Phase 1: Fetching basic underleases data");
      const { data, error } = await supabase
        .from('underleases')
        .select('*');

      if (error) {
        console.error('Error fetching underleases:', error);
        throw new Error(`Failed to load under-leases: ${error.message}`);
      }

      if (!data || data.length === 0) {
        console.log('No underlease data found');
        return []; // Return empty array instead of throwing error
      }

      console.log('Successfully fetched underleases:', data.length);
      
      // Basic transformation with placeholder names
      const basicTransformedData = data.map(item => {
        return {
          underlease_lease: item.underlease_lease,
          underlease_holder: item.underlease_holder,
          underlease_start: item.underlease_start,
          underlease_end: item.underlease_end,
          underlease_use: item.underlease_use,
          sublease_holder_name: 'Loading...'
        } as UnderLease;
      });
      
      // Phase 2: Try to fetch sublease holder names (if this fails, we fall back to phase 1 data)
      try {
        console.log("Phase 2: Fetching sublease and leaseholder data");
        
        // Get all the sublease IDs as numbers
        const subleaseIds = data.map(ul => parseInt(ul.underlease_lease))
                               .filter(id => !isNaN(id));
        
        if (subleaseIds.length === 0) {
          console.warn('No valid sublease IDs found to look up');
          return basicTransformedData;
        }
        
        // Fetch the subleases to find the leaseholder connections
        console.log("Fetching subleases with IDs:", subleaseIds);
        const { data: subleasesData, error: subleasesError } = await supabase
          .from('subleases')
          .select('lease_id, lease_holder')
          .in('lease_id', subleaseIds);
        
        if (subleasesError) {
          console.warn('Error fetching subleases:', subleasesError);
          return basicTransformedData;
        }
        
        if (!subleasesData || subleasesData.length === 0) {
          console.warn('No matching subleases found');
          return basicTransformedData;
        }
        
        console.log('Found matching subleases:', subleasesData.length);
        
        // Map sublease IDs to holder IDs
        const subleaseToHolderMap: Record<number, number> = {};
        subleasesData.forEach(sublease => {
          if (sublease.lease_id && sublease.lease_holder) {
            subleaseToHolderMap[sublease.lease_id] = sublease.lease_holder;
          }
        });
        
        // Get all the leaseholder IDs
        const holderIds = Object.values(subleaseToHolderMap)
                                .filter(id => id !== null && id !== undefined);
        
        if (holderIds.length === 0) {
          console.warn('No valid holder IDs found');
          return basicTransformedData;
        }
        
        // Fetch the leaseholders
        console.log("Fetching leaseholders with IDs:", holderIds);
        const { data: leaseholdersData, error: leaseholdersError } = await supabase
          .from('leaseholders')
          .select('holder_id, holder_name')
          .in('holder_id', holderIds);
        
        if (leaseholdersError) {
          console.warn('Error fetching leaseholders:', leaseholdersError);
          return basicTransformedData;
        }
        
        if (!leaseholdersData || leaseholdersData.length === 0) {
          console.warn('No matching leaseholders found');
          return basicTransformedData;
        }
        
        console.log('Found matching leaseholders:', leaseholdersData.length);
        
        // Map holder IDs to names
        const holderIdToNameMap: Record<number, string> = {};
        leaseholdersData.forEach(holder => {
          if (holder.holder_id && holder.holder_name) {
            holderIdToNameMap[holder.holder_id] = holder.holder_name;
          }
        });
        
        // Phase 3: Fetch the underlease holder names
        console.log("Phase 3: Fetching underlease holder names");
        
        // Get all the underlease holder IDs
        const underleaseHolderIds = data.map(ul => ul.underlease_holder)
                                      .filter(id => id !== null && id !== undefined);
        
        const uniqueHolderIds = [...new Set(underleaseHolderIds)];
        
        if (uniqueHolderIds.length === 0) {
          console.warn('No valid underlease holder IDs found');
          return basicTransformedData;
        }
        
        // Fetch the leaseholders for underlease holders
        console.log("Fetching leaseholders for underlease holders with IDs:", uniqueHolderIds);
        const { data: underleaseHoldersData, error: underleaseHoldersError } = await supabase
          .from('leaseholders')
          .select('holder_id, holder_name')
          .in('holder_id', uniqueHolderIds);
        
        if (underleaseHoldersError) {
          console.warn('Error fetching underlease holders:', underleaseHoldersError);
          return basicTransformedData;
        }
        
        if (!underleaseHoldersData || underleaseHoldersData.length === 0) {
          console.warn('No matching underlease holders found');
          return basicTransformedData;
        }
        
        console.log('Found matching underlease holders:', underleaseHoldersData.length);
        
        // Map underlease holder IDs to names
        const underleaseHolderIdToNameMap: Record<number, string> = {};
        underleaseHoldersData.forEach(holder => {
          if (holder.holder_id && holder.holder_name) {
            underleaseHolderIdToNameMap[holder.holder_id] = holder.holder_name;
          }
        });
        
        // Create the final transformed data with both sublease and underlease holder names
        const finalData = data.map(item => {
          // Get sublease holder name
          const leaseId = parseInt(item.underlease_lease);
          const subleaseHolderId = subleaseToHolderMap[leaseId];
          const subleaseHolderName = subleaseHolderId && holderIdToNameMap[subleaseHolderId] 
                             ? holderIdToNameMap[subleaseHolderId] : 'Unknown';
          
          // Get underlease holder name
          const underleaseHolderId = item.underlease_holder;
          const underleaseHolderName = underleaseHolderId && underleaseHolderIdToNameMap[underleaseHolderId]
                                     ? underleaseHolderIdToNameMap[underleaseHolderId] : 'Unknown';
          
          return {
            underlease_lease: item.underlease_lease,
            underlease_holder: item.underlease_holder,
            underlease_start: item.underlease_start,
            underlease_end: item.underlease_end,
            underlease_use: item.underlease_use,
            sublease_holder_name: subleaseHolderName,
            underlease_holder_name: underleaseHolderName
          } as UnderLease;
        });
        
        console.log('Successfully mapped leaseholder names to underleases');
        return finalData;
      } catch (innerError) {
        console.error('Error in phase 2 (fetching related data):', innerError);
        // If anything fails in phase 2, return the basic data
        return basicTransformedData;
      }
    } catch (error) {
      throw error instanceof Error 
        ? error 
        : new Error('An unexpected error occurred while loading under-leases');
    }
  },

  async create(data: CreateUnderLeaseData) {
    try {
      const { data: result, error } = await supabase
        .from('underleases')
        .insert([data])
        .select('*')
        .single();

      if (error) {
        throw new Error(`Failed to create under-lease: ${error.message}`);
      }

      if (!result) {
        throw new Error('Failed to create under-lease: No data returned');
      }

      return result;
    } catch (error) {
      throw error instanceof Error
        ? error
        : new Error('An unexpected error occurred while creating under-lease');
    }
  },

  async update(leaseId: number, holderId: number, startDate: string, data: Partial<CreateUnderLeaseData>) {
    try {
      const { data: result, error } = await supabase
        .from('underleases')
        .update(data)
        .match({ 
          underlease_lease: leaseId, 
          underlease_holder: holderId,
          underlease_start: startDate
        })
        .select('*')
        .single();

      if (error) {
        throw new Error(`Failed to update under-lease: ${error.message}`);
      }

      if (!result) {
        throw new Error('Failed to update under-lease: No data returned');
      }

      return result;
    } catch (error) {
      throw error instanceof Error
        ? error
        : new Error('An unexpected error occurred while updating under-lease');
    }
  },

  async delete(leaseId: number, holderId: number, startDate: string) {
    try {
      const { error } = await supabase
        .from('underleases')
        .delete()
        .match({ 
          underlease_lease: leaseId, 
          underlease_holder: holderId,
          underlease_start: startDate
        });

      if (error) {
        throw new Error(`Failed to delete under-lease: ${error.message}`);
      }

      return true;
    } catch (error) {
      throw error instanceof Error
        ? error
        : new Error('An unexpected error occurred while deleting under-lease');
    }
  }
};
